/**
 * RealtimeToastOverlay — Renders real-time toast notifications globally
 * 
 * Place once in App.jsx or DashboardLayout. Listens to useRealtimeNotifications
 * and renders floating toasts with auto-dismiss animation.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import useRealtimeNotifications from '@hooks/useRealtimeNotifications';

const TOAST_COLORS = {
  info: { bg: '#EFF6FF', border: '#3B82F6', icon: 'bi-info-circle', color: '#1D4ED8' },
  success: { bg: '#ECFDF5', border: '#22C55E', icon: 'bi-check-circle', color: '#059669' },
  warning: { bg: '#FFFBEB', border: '#F59E0B', icon: 'bi-exclamation-triangle', color: '#D97706' },
  error: { bg: '#FEF2F2', border: '#EF4444', icon: 'bi-x-circle', color: '#DC2626' },
  booking: { bg: '#F5F3FF', border: '#8B5CF6', icon: 'bi-calendar-check', color: '#7C3AED' },
  message: { bg: '#FDF2F8', border: '#EC4899', icon: 'bi-chat-dots', color: '#DB2777' },
  rsvp: { bg: '#ECFDF5', border: '#10B981', icon: 'bi-envelope-check', color: '#059669' },
  payment: { bg: '#ECFDF5', border: '#22C55E', icon: 'bi-credit-card', color: '#059669' },
  photo: { bg: '#FFF7ED', border: '#F97316', icon: 'bi-camera', color: '#EA580C' },
  vendor: { bg: '#F5F3FF', border: '#9B30FF', icon: 'bi-shop', color: '#7C3AED' },
};

const RealtimeToastOverlay = () => {
  const { toasts, dismissToast } = useRealtimeNotifications();
  const navigate = useNavigate();

  if (!toasts.length) return null;

  return (
    <div style={{
      position: 'fixed', top: 16, right: 16, zIndex: 10000, width: 360,
      display: 'flex', flexDirection: 'column', gap: 8,
      pointerEvents: 'none'
    }}>
      {toasts.map((toast, i) => {
        const config = TOAST_COLORS[toast.type] || TOAST_COLORS.info;
        return (
          <div key={toast.id} onClick={() => { if (toast.link) navigate(toast.link); dismissToast(toast.id); }}
            style={{
              pointerEvents: 'auto', cursor: toast.link ? 'pointer' : 'default',
              background: config.bg, border: `1px solid ${config.border}30`,
              borderLeft: `4px solid ${config.border}`,
              borderRadius: 12, padding: '12px 14px', boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
              animation: 'slideInRight 0.3s ease-out',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-4px)'; e.currentTarget.style.boxShadow = '0 6px 32px rgba(0,0,0,0.18)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.12)'; }}
          >
            <div className="d-flex gap-2 align-items-start">
              <span style={{ fontSize: '1.1rem' }}>{toast.icon || <i className={`bi ${config.icon}`} style={{ color: config.color }}/>}</span>
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="fw-bold" style={{ fontSize: '0.8rem', color: config.color }}>{toast.title}</div>
                  <button onClick={e => { e.stopPropagation(); dismissToast(toast.id); }}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9CA3AF', fontSize: '0.8rem', lineHeight: 1 }}>
                    <i className="bi bi-x"/>
                  </button>
                </div>
                {toast.message && <div style={{ fontSize: '0.75rem', color: '#6B7280', lineHeight: 1.4, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{toast.message}</div>}
              </div>
            </div>
          </div>
        );
      })}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default RealtimeToastOverlay;
